import { Component } from './Component';

type ComponentClass<T extends Component = Component> = new (...args: unknown[]) => T;

/**
 * An entity is a container for components.
 * Entities themselves have no data or behavior - they're just IDs with components attached.
 */
export class Entity {
  private static _nextId = 0;

  readonly id: number;
  private _components = new Map<string, Component>();
  private _destroyed = false;
  private _onDestroy?: () => void;

  constructor() {
    this.id = Entity._nextId++;
  }

  /**
   * Add a component to this entity.
   */
  add<T extends Component>(component: T): this {
    this._components.set(component.type, component);
    return this;
  }

  /**
   * Get a component by its class.
   * Throws if the component doesn't exist.
   */
  get<T extends Component>(componentClass: ComponentClass<T>): T {
    const component = this._components.get(componentClass.name);
    if (!component) {
      throw new Error(`Entity ${this.id} does not have component ${componentClass.name}`);
    }
    return component as T;
  }

  /**
   * Check if this entity has a component.
   */
  has(componentClass: ComponentClass): boolean {
    return this._components.has(componentClass.name);
  }

  /**
   * Check if this entity has all the specified components.
   */
  hasAll(componentClasses: ComponentClass[]): boolean {
    return componentClasses.every((c) => this.has(c));
  }

  /**
   * Remove a component from this entity.
   */
  remove(componentClass: ComponentClass): this {
    this._components.delete(componentClass.name);
    return this;
  }

  /**
   * Mark this entity for destruction.
   * It will be removed from the world on the next update.
   */
  destroy(): void {
    this._destroyed = true;
    this._onDestroy?.();
  }

  /**
   * Check if this entity is marked for destruction.
   */
  get destroyed(): boolean {
    return this._destroyed;
  }

  /**
   * Set a callback to be called when this entity is destroyed.
   * Used internally by the World.
   */
  _setOnDestroy(callback: () => void): void {
    this._onDestroy = callback;
  }
}
